import { FC } from 'react';
import { FormControl } from 'react-bootstrap';
import { $Formutil, connect, EasyField } from 'react-bootstrap-formutil';
import FormGroup from 'components/FormGroup';
import FormLayout from 'components/FormLayout';
import AddressInfo from 'components/forms/AddressInfo';
import CountrySelect from 'components/forms/CountrySelect';
import Email from 'components/forms/Email';
import FormSelect, { transformLegacyOptions } from 'components/FormSelect';
import RadioFormutil, { type RadioFormutilProps } from 'components/Formutil/RadioFormutil';
import TelcodesPhone from 'components/TelPhone';
import { Fade } from 'components/Transition';
import type { FormCheckType } from 'types/common';
import type { CustomerInfoKeyType } from 'types/customer';
import config from 'utils/config';
import Pattern from 'utils/patterns';
import './style.scss';

const commonOptions: RadioFormutilProps['options'] = [
    {
        label: __('Yes'),
        value: '1'
    },
    {
        label: __('No'),
        value: '0'
    }
];

interface ComplianceFieldType {
    temporary_regulatory_members_checked: FormCheckType;
    temporary_regulatory_stockholder_checked: FormCheckType;
    regulatory_company_other: {
        company_name: string;
        company_address: string;
        company_city: string;
        company_state_code: string;
        company_country: string;
    };
    regulatory_relationship: string;
    temporary_employee_track_checked?: FormCheckType;
    regulatory_stockholder: string;
    regulatory_members: string;
}

export function getFieldsOfCompliance(): CustomerInfoKeyType[] {
    return [
        'regulatory_relationship',
        'regulatory_members',
        'regulatory_company_other',
        'regulatory_stockholder',
        'temporary_regulatory_members_checked',
        'temporary_regulatory_stockholder_checked',
        'temporary_employee_track_checked'
    ];
}

interface ComplianceProps {
    $formutil: $Formutil<ComplianceFieldType>;
    disabled?: boolean;
}

interface ComplianceValidMessage {
    name: { required: string; isName: string };
    symbol: { required: string; isSymbol: string };
    occupation: { required: string; isOccupation: string };
    number: { required: string; isNumber: string };
}

// 自定义校验器函数类型：运行时 validMessage 始终存在，返回 true 表示校验通过，返回 string 为错误信息
type ComplianceValidatorFunc = (
    value: string,
    prop: any,
    fieldProps: { validMessage: Record<string, string> } & Record<string, any>
) => string | true;

interface ComplianceCustomValidators {
    isName: ComplianceValidatorFunc;
    isSymbol: ComplianceValidatorFunc;
    isOccupation: ComplianceValidatorFunc;
    isNumber: ComplianceValidatorFunc;
}

const validMessage: ComplianceValidMessage = {
    name: {
        required: config.inputError(__("family member's name")),
        isName: __('The name you entered is not valid')
    },
    symbol: {
        required: config.inputTheError(__('stock symbol')),
        isSymbol: __('The stock symbol you entered is not valid')
    },
    occupation: {
        required: config.selectError(__('Occupation')),
        isOccupation: __('The Occupation you entered is not valid')
    },
    number: {
        required: config.selectError(__('Years of Employment')),
        isNumber: __('The Years of Employment you entered is not valid')
    }
};

const $validators: ComplianceCustomValidators = {
    isName: (value, _prop, { validMessage: { isName } }) => {
        if (/^[a-zA-Z\-\s']+$/g.test(value)) {
            return true;
        }

        return isName;
    },
    isSymbol: (value, _prop, { validMessage: { isSymbol } }) => {
        if (/^[a-zA-Z\d\s,;]+$/g.test(value)) {
            return true;
        }

        return isSymbol;
    },
    isOccupation: (value, _prop, { validMessage: { isOccupation } }) => {
        if (/^[a-zA-Z\s']+$/g.test(value)) {
            return true;
        }

        return isOccupation;
    },
    isNumber: (value, _prop, { validMessage: { isNumber } }) => {
        if (Pattern.isNumber(value)) {
            return true;
        }

        return isNumber;
    }
};

const Compliance: FC<ComplianceProps> = ({ $formutil, disabled }) => {
    const { $params, $setValues } = $formutil;

    return (
        <div className="compliance-root">
            <h3 className="open-title">{__('REGULATORY INFORMATION')}</h3>
            <RadioFormutil
                label={__(
                    'Is the account holder or any immediate family member who resides in the same household, registered as a broker-dealer or an employee, director or owner of a securities or commodities brokerage firm, employee of a bank, hedge fund, or other financial services company?'
                )}
                name="temporary_regulatory_members_checked"
                $defaultValue="0"
                options={commonOptions}
                disabled={disabled}
                reverse={false}
                needUnderLine={false}
                inline
            />
            {/* 如果temporary_regulatory_members_checked选择否则regulatory_relationship传-1，代表要清除regulatory_relationship、regulatory_members、regulatory_company_other等字段已有的值 */}
            {$params.temporary_regulatory_members_checked === '0' && (
                <EasyField name="regulatory_relationship" defaultValue="-1" type="hidden" />
            )}
            <Fade in={$params.temporary_regulatory_members_checked === '1'} exit={false}>
                <div>
                    <FormGroup
                        name="regulatory_relationship"
                        required
                        validMessage={{ required: config.inputError(__('relationship to account holder')) }}
                        label={__('Relationship To Account Holder')}>
                        <FormSelect
                            disabled={disabled}
                            placeholder={__('Please Select')}
                            options={transformLegacyOptions(config.Relationship, 'regulatory_relationship')}
                        />
                    </FormGroup>
                    <Fade in={Number($params.regulatory_relationship) > 0} exit={false}>
                        <FormGroup
                            name="regulatory_members"
                            required
                            isName
                            $validators={$validators}
                            validMessage={validMessage.name}
                            label={__('Name of The Family Member')}>
                            <FormControl disabled={disabled} placeholder={__('Description')} />
                        </FormGroup>
                    </Fade>
                    <RadioFormutil
                        label={__('Employee Track program')}
                        name="temporary_employee_track_checked"
                        $defaultValue="0"
                        options={commonOptions}
                        disabled={disabled}
                        reverse={false}
                        needUnderLine={false}
                        inline
                    />
                    <Fade in={$params.temporary_employee_track_checked === '1'} exit={false}>
                        <div>
                            <FormGroup
                                label={__('Company Name')}
                                name="regulatory_company_other.company"
                                required
                                validMessage={{ required: config.selectError(__('company Name')) }}>
                                <FormControl disabled={disabled} placeholder={__('Company Name')} />
                            </FormGroup>
                            <FormLayout layout="1:1" noFlexInMobile>
                                <FormGroup
                                    name="regulatory_company_other.career"
                                    label={__('Occupation')}
                                    required
                                    isOccupation
                                    $validators={$validators}
                                    validMessage={validMessage.occupation}>
                                    <FormControl disabled={disabled} placeholder={__('Occupation')} />
                                </FormGroup>
                                <FormGroup
                                    name="regulatory_company_other.years_of_employment"
                                    label={__('Years of Employment')}
                                    required
                                    isNumber
                                    $validators={$validators}
                                    validMessage={validMessage.number}>
                                    <FormControl disabled={disabled} placeholder={__('Years of Employment')} />
                                </FormGroup>
                            </FormLayout>
                            <Email
                                disabled={disabled}
                                name="regulatory_company_other.company_email"
                                label={__('Company Email')}
                            />
                            <FormGroup name="regulatory_company_other.company_phone" label={__('Company Phone Number')}>
                                <TelcodesPhone disabled={disabled} />
                            </FormGroup>
                            <AddressInfo
                                isComboName={true}
                                prefixStreetPlaceholder={__('Company')}
                                prefix="regulatory_company_other.company"
                                isProvince={false}
                                disabled={disabled}
                            />
                            <FormGroup
                                label={__('Country')}
                                name="regulatory_company_other.company_country"
                                required={false}
                                // 从AddressInfo中拆分出来，company_country的历史逻辑默认是美国且禁用
                                $defaultValue="USA"
                                $onFieldChange={() =>
                                    $setValues({
                                        regulatory_company_other: {
                                            ...($params.regulatory_company_other || {}),
                                            company_state_code: '',
                                            company_city: ''
                                        }
                                    })
                                }>
                                <CountrySelect isSSN disabled fieldName="regulatory_company_other.company_country" />
                            </FormGroup>
                        </div>
                    </Fade>
                </div>
            </Fade>
            <RadioFormutil
                label={__(
                    'Is the account holder or any immediate family member a senior executive or 10% or greater shareholder of a publicly listed company?'
                )}
                name="temporary_regulatory_stockholder_checked"
                $defaultValue="0"
                options={commonOptions}
                disabled={disabled}
                reverse={false}
                needUnderLine={false}
                inline
            />
            <Fade in={$params.temporary_regulatory_stockholder_checked === '1'} exit={false}>
                <FormGroup
                    name="regulatory_stockholder"
                    required
                    isSymbol
                    $validators={$validators}
                    label={__('Enter the stick symbol(s) of the company or companies, separated by commas:')}
                    validMessage={validMessage.symbol}>
                    <FormControl disabled={disabled} placeholder={__('Enter')} />
                </FormGroup>
            </Fade>
        </div>
    );
};

export default connect(Compliance);
